import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef } from 'react'
import { fontSans, fontMono, hexToRgba } from './theme'

const ACCENT = '#C4B5FD'

function ChatBubble({
  from,
  children,
  delay,
}: {
  from: 'user' | 'kaavya'
  children: React.ReactNode
  delay: number
}) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-40px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      style={{
        display: 'flex',
        justifyContent: from === 'user' ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        style={{
          maxWidth: '88%',
          background:
            from === 'user'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(124, 92, 246, 0.09)',
          border: `1px solid ${
            from === 'user'
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(139, 108, 255, 0.3)'
          }`,
          borderRadius:
            from === 'user' ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
          padding: '11px 15px',
          fontFamily: fontSans,
          fontSize: '0.84rem',
          lineHeight: 1.6,
          color: 'rgba(255,255,255,0.82)',
        }}
      >
        {children}
      </div>
    </motion.div>
  )
}

function ConversationCard() {
  return (
    <div
      style={{
        background:
          'linear-gradient(160deg, rgba(124,92,246,0.06), rgba(10,15,31,0.5))',
        border: '1px solid rgba(139, 108, 255, 0.25)',
        borderRadius: '20px',
        padding: '1.6rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-70px',
          right: '-70px',
          width: '240px',
          height: '240px',
          background:
            'radial-gradient(circle, rgba(139,108,255,0.16) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* header with the Kaavya logo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          paddingBottom: '0.9rem',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          marginBottom: '1rem',
        }}
      >
        <img
          src="/Kaavya_Agent.png"
          alt="Kaavya agent logo"
          style={{
            width: '68px',
            height: 'auto',
            borderRadius: '11px',
            flexShrink: 0,
            boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
          }}
        />
        <div>
          <div
            style={{
              fontFamily: fontSans,
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#fff',
            }}
          >
            Kaavya
          </div>
          <div
            style={{
              fontFamily: fontSans,
              fontSize: '0.7rem',
              color: ACCENT,
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#2DD4BF',
                boxShadow: '0 0 8px rgba(45,212,191,0.8)',
              }}
            />
            Agentic AI · across all products
          </div>
        </div>
      </div>

      {/* conversation */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.8rem',
        }}
      >
        <ChatBubble from="user" delay={0.1}>
          Which of our tables contain customer PII?
        </ChatBubble>

        <ChatBubble from="kaavya" delay={0.25}>
          <div style={{ marginBottom: '8px' }}>
            Scanned the catalog — 2 tables carry regulated fields:
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '7px',
            }}
          >
            <div style={findingChip}>
              <span style={mono}>crm.accounts</span>
              <span style={{ ...tiny, color: '#F5B850' }}>
                email · phone 🔒
              </span>
            </div>
            <div style={findingChip}>
              <span style={mono}>billing.subscriptions</span>
              <span style={{ ...tiny, color: '#F5B850' }}>card_number 🔒</span>
            </div>
          </div>
        </ChatBubble>

        <ChatBubble from="user" delay={0.4}>
          Why did last night&apos;s billing pipeline fail?
        </ChatBubble>

        <ChatBubble from="kaavya" delay={0.55}>
          <div style={{ marginBottom: '8px' }}>
            Root cause:{' '}
            <span style={{ color: 'rgba(255,255,255,0.95)' }}>
              schema drift
            </span>{' '}
            — <code style={mono}>billing.subscriptions</code> added a nullable
            column; the <code style={mono}>not_null(card_number)</code> rule
            failed on 3 rows. Fix suggested.
          </div>
          <span
            style={{
              ...tiny,
              color: '#2DD4BF',
              border: '1px solid rgba(45,212,191,0.3)',
              borderRadius: '100px',
              padding: '3px 10px',
              background: 'rgba(45,212,191,0.06)',
              display: 'inline-block',
            }}
          >
            diagnosed from run logs ✓
          </span>
        </ChatBubble>

        <ChatBubble from="user" delay={0.7}>
          Show me all active customers.
        </ChatBubble>

        <ChatBubble from="kaavya" delay={0.85}>
          <div>
            42 customers — emails hash-masked per policy, served through
            Ontext&apos;s governed ontology.
          </div>
          <span
            style={{
              ...tiny,
              color: 'rgba(45,212,191,0.85)',
              border: '1px solid rgba(45,212,191,0.3)',
              borderRadius: '100px',
              padding: '3px 10px',
              background: 'rgba(45,212,191,0.06)',
              display: 'inline-block',
              marginTop: '8px',
            }}
          >
            audit event #4821 logged ✓
          </span>
        </ChatBubble>
      </div>
    </div>
  )
}

const mono: React.CSSProperties = {
  fontFamily: fontMono,
  fontSize: '0.72rem',
  color: '#d8ccff',
}

const tiny: React.CSSProperties = {
  fontFamily: fontSans,
  fontSize: '0.68rem',
}

const findingChip: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '9px',
  padding: '8px 11px',
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
}

const highlights = [
  'Answers from your governed catalog — in plain English',
  'PII-aware by default: never shows personal data unmasked',
  'Explains failures, suggests fixes, logs every answer for audit',
]

export function Kaavya() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      id="kaavya"
      style={{
        padding: '7rem 2rem',
        background:
          'linear-gradient(180deg, #0A0F1F 0%, #100d24 50%, #0A0F1F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${hexToRgba(ACCENT, 0.45)}, transparent)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '-220px',
          width: '520px',
          height: '520px',
          background: `radial-gradient(circle, ${hexToRgba(ACCENT, 0.06)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '4rem',
            alignItems: 'center',
          }}
        >
          {/* Left: logo + short story */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Kaavya logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
              style={{
                width: 'min(340px, 78%)',
                marginBottom: '1.75rem',
              }}
            >
              <img
                src="/Kaavya_Agent.png"
                alt="Kaavya — the SynKrasis AI agent"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
                }}
              />
            </motion.div>

            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: ACCENT,
                display: 'inline-block',
                padding: '5px 14px',
                borderRadius: '100px',
                background: hexToRgba(ACCENT, 0.1),
                border: `1px solid ${hexToRgba(ACCENT, 0.3)}`,
                marginBottom: '1.1rem',
              }}
            >
              Meet Kaavya · The AI Agent
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(1.9rem, 3.6vw, 2.8rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.12,
                marginBottom: '1.1rem',
              }}
            >
              Agentic AI that solves{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, #4A90E2, #9D4EDD, #2DD4BF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                complex data problems.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.75,
                marginBottom: '1.5rem',
                maxWidth: '480px',
              }}
            >
              Kaavya is the agentic AI woven through every SynKrasis product.
              It understands your data, your rules and your policies — so it
              finds the PII, explains the failure, and answers the question
              for you.
            </p>

            {/* three crisp one-liners */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.7rem',
                marginBottom: '2rem',
              }}
            >
              {highlights.map((h, i) => (
                <motion.div
                  key={h}
                  initial={{ opacity: 0, y: 12 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.45, delay: 0.25 + i * 0.08 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '7px',
                      background: hexToRgba(ACCENT, 0.14),
                      border: `1px solid ${hexToRgba(ACCENT, 0.35)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: ACCENT,
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 8.2 6.5 10.7 12 5"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span
                    style={{
                      fontFamily: fontSans,
                      fontSize: '0.9rem',
                      color: 'rgba(255,255,255,0.72)',
                      lineHeight: 1.5,
                    }}
                  >
                    {h}
                  </span>
                </motion.div>
              ))}
            </div>

            <a
              href="#demo"
              style={{
                fontFamily: fontSans,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#ffffff',
                textDecoration: 'none',
                padding: '0.75rem 1.6rem',
                borderRadius: '10px',
                background:
                  'linear-gradient(135deg, #4A90E2, #9D4EDD, #2DD4BF)',
                boxShadow: '0 0 30px rgba(139,108,255,0.3)',
                transition: 'all 0.25s ease',
                display: 'inline-block',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow =
                  '0 8px 36px rgba(139,108,255,0.45)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow =
                  '0 0 30px rgba(139,108,255,0.3)'
              }}
            >
              Talk to Kaavya — Book a Demo
            </a>
          </motion.div>

          {/* Right: conversation */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{
              duration: 0.7,
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <ConversationCard />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
